/**
 * D-WEB-SHARED-ERROR-UTILS1 — crash-safe timestamp helpers, extracted from
 * `apps/web/src/lib/format.ts` (Sprint 16 Wave H / D-WEB-SAFE-TIMESTAMP1) so
 * `apps/admin`'s byte-identical restatement in `locale-format.ts` (comment:
 * "restated here rather than imported, because the apps do not import across
 * their boundary") has an actual shared home to import from instead.
 *
 * Framework-free: no React, no fetch, no app-specific error classes.
 */

/**
 * The neutral stand-in for a server timestamp that cannot be read. Both apps
 * already rendered exactly this dash by hand before this package existed.
 */
export const UNREADABLE_TIMESTAMP_PLACEHOLDER = "—";

/**
 * Crash-safe wrapper for every place that turns a SERVER timestamp into text.
 *
 * `Intl.DateTimeFormat#format` throws a RangeError on an invalid Date and
 * takes the whole view down with it; `Date#toLocale*String` does not throw
 * but renders the literal text "Invalid Date". A readable value goes through
 * the caller's own formatter unchanged, so the output for good data is
 * exactly what the call site produced before. Anything unreadable — an
 * unparseable string, an empty string, null, undefined, NaN, an Invalid
 * Date — returns the placeholder and never reaches the formatter.
 *
 * Only the value is checked. A formatter that is itself misconfigured (a bad
 * locale, say) still throws, so a real bug is not turned into a dash.
 */
export function formatTimestampSafe(
  value: string | number | Date | null | undefined,
  format: (date: Date) => string,
  placeholder: string = UNREADABLE_TIMESTAMP_PLACEHOLDER,
): string {
  if (value === null || value === undefined) return placeholder;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return placeholder;
  return format(date);
}

/**
 * Safe date string → ISO converter for a value that goes on the WIRE (a
 * query parameter, a request body), as opposed to `formatTimestampSafe`,
 * which makes text for the screen.
 *
 * `new Date("abc").toISOString()` throws `RangeError: Invalid time value`,
 * which crashes the entire component on render. Operators can easily land
 * here via manual URL typing, corrupted bookmarks, saved views or shared
 * links from before the schema settled. Returns `null` on an unreadable
 * value so callers can omit the filter cleanly. It shares only the validity
 * test with `formatTimestampSafe`; the result types differ (nullable ISO vs.
 * display text with a placeholder), so the two are not merged.
 */
export function safeDateToISO(raw: string): string | null {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
